/**
 * Admin All Data Files Page
 * ყველა ფაილის ადმინისტრირების გვერდი
 */

import { useState } from 'react';
import { formatDate } from '../../utils/formatDate';
import { useAdminDataFiles } from '../../hooks/useAdminListing';
import type { DataFileAdminListResponse } from '../../features/api/adminListing';

export default function AllDataFilesPage() {
  const [statusFilter, setStatusFilter] = useState('');
  const [fileTypeFilter, setFileTypeFilter] = useState('');
  const [page, setPage] = useState(0);

  const { dataFiles, totalElements, totalPages, isLoading } = useAdminDataFiles({
    status: statusFilter || undefined,
    fileType: fileTypeFilter || undefined,
    page,
    size: 20,
  });

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'READY':
        return <span className="px-2 py-1 text-xs rounded-full bg-green-100 text-green-800">Ready</span>;
      case 'PENDING':
        return <span className="px-2 py-1 text-xs rounded-full bg-yellow-100 text-yellow-800">Pending</span>;
      case 'PROCESSING':
        return <span className="px-2 py-1 text-xs rounded-full bg-blue-100 text-blue-800">Processing</span>;
      case 'FAILED':
        return <span className="px-2 py-1 text-xs rounded-full bg-red-100 text-red-800">Failed</span>;
      case 'DELETED':
        return <span className="px-2 py-1 text-xs rounded-full bg-gray-100 text-gray-800">Deleted</span>;
      default:
        return <span className="px-2 py-1 text-xs rounded-full bg-gray-100 text-gray-800">{status}</span>;
    }
  };

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">All Data Files</h1>
        <p className="text-gray-600">View all platform data files</p>
      </div>

      {/* Filters */}
      <div className="flex gap-4 items-center">
        <select
          value={statusFilter}
          onChange={(e) => { setStatusFilter(e.target.value); setPage(0); }}
          className="px-4 py-2 border border-gray-300 rounded-lg bg-white"
          aria-label="Filter by status"
        >
          <option value="">All Statuses</option>
          <option value="READY">Ready</option>
          <option value="PENDING">Pending</option>
          <option value="PROCESSING">Processing</option>
          <option value="FAILED">Failed</option>
        </select>
        <select
          value={fileTypeFilter}
          onChange={(e) => { setFileTypeFilter(e.target.value); setPage(0); }}
          className="px-4 py-2 border border-gray-300 rounded-lg bg-white"
          aria-label="Filter by file type"
        >
          <option value="">All Types</option>
          <option value="PDF">PDF</option>
          <option value="WORD">Word</option>
          <option value="EXCEL">Excel</option>
          <option value="TEXT">Text</option>
          <option value="OTHER">Other</option>
        </select>
        <div className="ml-auto text-sm text-gray-500">
          {totalElements} files found
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">File</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">User</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Size</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Vectorized</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Chunks</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Created</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {dataFiles.length === 0 ? (
              <tr>
                <td colSpan={8} className="px-6 py-12 text-center text-gray-500">
                  No files found
                </td>
              </tr>
            ) : (
              dataFiles.map((file: DataFileAdminListResponse) => (
                <tr key={file.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4">
                    <div className="font-medium text-gray-900 truncate max-w-xs" title={file.fileName}>
                      {file.fileName}
                    </div>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-600">{file.userEmail || '-'}</td>
                  <td className="px-6 py-4 text-sm text-gray-600">{file.fileType || '-'}</td>
                  <td className="px-6 py-4 text-sm text-gray-600">{formatFileSize(file.fileSize)}</td>
                  <td className="px-6 py-4">{getStatusBadge(file.status)}</td>
                  <td className="px-6 py-4">
                    {file.vectorized ? (
                      <span className="text-green-600">Yes</span>
                    ) : (
                      <span className="text-gray-400">No</span>
                    )}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-600">{file.chunkCount || '-'}</td>
                  <td className="px-6 py-4 text-sm text-gray-500">
                    {formatDate(file.createdAt)}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex justify-center gap-2">
          <button
            onClick={() => setPage(p => Math.max(0, p - 1))}
            disabled={page === 0}
            className="px-3 py-1 border rounded disabled:opacity-50"
          >
            Previous
          </button>
          <span className="px-3 py-1">Page {page + 1} of {totalPages}</span>
          <button
            onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
            disabled={page >= totalPages - 1}
            className="px-3 py-1 border rounded disabled:opacity-50"
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
}
